import { z } from 'zod';

const months = z.number().int().min(1).max(12);
const recurringStream = z
  .object({
    enabled: z.boolean().default(true),
    method: z.enum(['LAST_POSTED', 'TRAILING_AVERAGE']).default('LAST_POSTED'),
    maxAgeMonths: months.default(12),
    trailingMonths: months.default(3),
  })
  .strict();

/** Persisted, visible defaults; the same contract is consumed by API, UI and MCP. */
export const PayrollRunRateConfigSchema = z
  .object({
    version: z.literal(1).default(1),
    clt: recurringStream.prefault({}),
    rpa: z
      .object({
        enabled: z.boolean().default(true),
        trailingMonths: months.default(3),
        minimumOccurrences: months.default(2),
      })
      .strict()
      .prefault({}),
    proLabore: recurringStream.prefault({}),
    benefits: z
      .object({
        transport: z.boolean().default(true),
        meals: z.boolean().default(true),
        health: z.boolean().default(true),
      })
      .strict()
      .prefault({}),
    variableCompensation: z
      .object({
        bonus: z.boolean().default(false),
        commission: z.boolean().default(false),
        overtime: z.boolean().default(false),
        trailingMonths: months.default(3),
      })
      .strict()
      .prefault({}),
    includePlannedPositions: z.boolean().default(false),
  })
  .strict()
  .superRefine((config, context) => {
    if (config.rpa.minimumOccurrences > config.rpa.trailingMonths) {
      context.addIssue({
        code: 'custom',
        path: ['rpa', 'minimumOccurrences'],
        message: 'Minimum occurrences cannot exceed the trailing month window',
      });
    }
  });

export type PayrollRunRateConfigInput = z.input<typeof PayrollRunRateConfigSchema>;
export type PayrollRunRateConfig = z.output<typeof PayrollRunRateConfigSchema>;

export function resolvePayrollRunRateConfig(
  input?: PayrollRunRateConfigInput,
): PayrollRunRateConfig {
  return PayrollRunRateConfigSchema.parse(input ?? {});
}

export interface PayrollScenarioImpactResponse {
  fingerprint: string;
  scenarioRevision: string;
  targetScenarioRevision?: string;
  affectedBudgetCount: number;
  budgets: {
    budgetId: string;
    name: string;
    entityId: string;
    currency: string;
    months: { period: string; before: string; after: string; difference: string }[];
    coverage: string[];
    unallocated: { before: string; after: string; difference: string };
  }[];
}

export interface BudgetPayrollScenarioResponse {
  forecastRuleSetId: string;
  name: string;
  revision: string;
  updatedAt: string;
  origin: 'EXPLICIT' | 'ENTITY_DEFAULT';
  fallback: boolean;
}

export interface SetBudgetPayrollScenarioRequest {
  forecastRuleSetId: string;
  impactFingerprint: string;
  changeReason: string;
}
